// Tiny typed event bus. Gameplay systems emit semantic events; the render,
// audio, and UI layers subscribe to turn them into juice (particles, floating
// numbers, screenshake, sound) and screen transitions. Keeping this one-way
// (logic emits, presentation listens) means the simulation never reaches into
// Pixi or the DOM directly.

import type { ResourceId } from '@data/resources';
import type { BuildingId } from '@data/buildings';
import type { AgeCard } from '@data/cards';

/** All event names and their payload shapes. */
export interface GameEvents {
  // A harvest tick dealt damage to a node at tile (gx, gy).
  'harvest:hit': { gx: number; gy: number; resource: ResourceId; crit: boolean };
  // A node was fully depleted and dropped its resource.
  'harvest:drop': { gx: number; gy: number; resource: ResourceId; amount: number };
  // The settlement auto-placed a building.
  'building:built': { gx: number; gy: number; building: BuildingId; level: number };
  // The civilization advanced an age; UI should show the card picker.
  'age:advance': { age: number; cards: AgeCard[] };
  // The player committed to a card.
  'card:picked': { card: AgeCard };
  // The era boss has been summoned to the gate (progress crossed the age
  // threshold). The next wave to telegraph is the boss; beating it advances the age.
  'gate:summon': { age: number; era: number; boss: string };
  // A wave is about to attack (countdown reached zero).
  'wave:incoming': { wave: number; isBoss: boolean };
  // A wave resolved. win=true means the settlement held.
  'wave:result': {
    wave: number;
    win: boolean;
    defense: number;
    threat: number;
    rewardSpoils: number;
    isBoss: boolean;
  };
  // Generic celebratory popup (milestones, unlocks, evolutions).
  milestone: { title: string; subtitle?: string; tone?: 'good' | 'bad' | 'epic' };
  // The settlement reached a new visual tier (tents -> village -> ...).
  'settlement:evolve': { tier: number; name: string };
  // A special technology was purchased with spoils.
  'tech:bought': { techId: string; name: string; isBuff: boolean };
  // A boss dropped an artifact (not yet activated).
  'artifact:dropped': { id: string; name: string };
  // An artifact was activated with resources.
  'artifact:activated': { id: string; name: string };
  // A random calamity struck the settlement (not a wave: nature/economy/society).
  calamity: { id: string; icon: string; name: string };
  // The run ended (lost a wave). UI shows the skill-point screen.
  death: { wave: number; skillPointsEarned: number };
  // A fresh run started (after spending skill points or hard reset).
  restart: { skillPoints: number };
  // The current planet's conquest goal was reached; the player may ascend.
  'conquer:available': { planet: number; wave: number };
  // Planetary prestige: a planet was conquered for cosmic currency.
  prestige: { planet: number; cosmicGain: number; cosmicTotal: number };
  // Low-level juice cues the render layer acts on directly.
  shake: { intensity: number };
  flash: { color: number; alpha: number };
}

type Handler<T> = (payload: T) => void;

class EventBus {
  private handlers = new Map<keyof GameEvents, Set<Handler<unknown>>>();

  /** Subscribe to an event. Returns an unsubscribe function. */
  on<K extends keyof GameEvents>(type: K, handler: Handler<GameEvents[K]>): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    set.add(handler as Handler<unknown>);
    return () => set!.delete(handler as Handler<unknown>);
  }

  /** Fire an event. Handler exceptions are isolated so one bad listener cannot
   *  break the simulation tick. */
  emit<K extends keyof GameEvents>(type: K, payload: GameEvents[K]): void {
    const set = this.handlers.get(type);
    if (!set) return;
    for (const handler of set) {
      try {
        (handler as Handler<GameEvents[K]>)(payload);
      } catch (err) {
        console.error(`[events] handler for "${String(type)}" threw:`, err);
      }
    }
  }
}

/** Single shared bus for the whole game. */
export const bus = new EventBus();
